import { ChevronDown } from "lucide-react";
import { motion, type Variants } from "motion/react";
import { useState } from "react";

import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

const EASE = [0.25, 0.1, 0.25, 1] as const;

const headerVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
};

const headerItemVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const listVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

const viewport = { once: true, margin: "-80px" } as const;

const ITEMS: { question: string; answer: string }[] = [
  {
    question: m.landing_faq_agent_question(),
    answer: m.landing_faq_agent_answer(),
  },
  {
    question: m.landing_faq_sovereignty_question(),
    answer: m.landing_faq_sovereignty_answer(),
  },
  {
    question: m.landing_faq_library_question(),
    answer: m.landing_faq_library_answer(),
  },
  {
    question: m.landing_faq_crm_question(),
    answer: m.landing_faq_crm_answer(),
  },
  {
    question: m.landing_faq_ideas_question(),
    answer: m.landing_faq_ideas_answer(),
  },
  {
    question: m.landing_faq_entitlements_question(),
    answer: m.landing_faq_entitlements_answer(),
  },
];

/**
 * FAQ accordion. Multiple items can be open at once. Answers a research or
 * technology team's first questions about sovereignty, entitlements, and how
 * the layer sits over an existing library.
 */
export const FaqSection = () => {
  const [open, setOpen] = useState<Set<number>>(new Set([0]));

  const toggle = (index: number) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <section
      id="faq"
      data-tour="landing-faq"
      className="mx-auto w-full max-w-[1320px] scroll-mt-20 px-5 py-12 md:py-20 z-10 relative"
    >
      <div className="w-[300px] h-[500px] absolute top-[150px] left-1/2 -translate-x-1/2 origin-top-left rotate-[-33.39deg] bg-primary/10 blur-[100px] z-0"></div>

      <motion.div
        className="mx-auto flex max-w-2xl flex-col items-center gap-4 text-center"
        variants={headerVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        <motion.h2
          variants={headerItemVariants}
          className="text-3xl font-semibold leading-tight text-foreground md:text-4xl"
        >
          {m.landing_faq_title()}
        </motion.h2>
        <motion.p variants={headerItemVariants} className="max-w-xl text-sm font-medium text-muted-foreground">
          {m.landing_faq_subtitle()}
        </motion.p>
      </motion.div>

      <motion.div
        className="mx-auto mt-10 flex max-w-[640px] flex-col gap-4"
        variants={listVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        {ITEMS.map((item, index) => {
          const isOpen = open.has(index);
          return (
            <motion.div
              key={item.question}
              variants={itemVariants}
              className="overflow-hidden rounded-[10px] border border-border bg-card/40 shadow-sm transition-colors hover:bg-card/60"
            >
              <button
                type="button"
                onClick={() => toggle(index)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
              >
                <span className="text-base font-medium text-foreground">{item.question}</span>
                <ChevronDown
                  className={cn(
                    "size-5 shrink-0 text-muted-foreground transition-all duration-300",
                    isOpen && "rotate-180 scale-110",
                  )}
                />
              </button>
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300",
                  isOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0",
                )}
              >
                <div className="px-5 pb-4">
                  <p className="text-sm leading-6 text-muted-foreground">{item.answer}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
};
